import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { axiosClient } from "@/helpers/auth";
import { useNotificationsStore } from "@/store/notificationsStore";
import { useGlobalStore } from '@/store/storeGlobal';
import { useWorklistSearchStore } from '@/store/search/worklistSearchStore';

// Recursive helper function for mapping all children of the given item
// Defined outside store for better performance (doesn't use store scope)
function getAllSystemObjects (item, accumulator)
{
  // Opprett ny accumulator hvis ikke gitt (for standalone-kall)
  const acc = accumulator ?? new Map();
  acc.set(item.systemObjectId, item);
  if (item.items) {
    for (const child of item.items) {
      getAllSystemObjects(child, acc);
    }
  }
  return acc;
}

export const useMenuStore = defineStore("menuStore", () =>
{
  const notificationsStore = useNotificationsStore();

  const globalStore = useGlobalStore();

  const menuItems = ref(null);
  const mapOfSystemObjects = ref();

  // getRoute information for a systemobject
  const getRoute = (item) =>
  {
    switch (item.objectType) {
      case 'folder':
        return {
          path: `/folder/${item.systemObjectId || ''}`,
          exact: true
        };
      case 'system-object':
        return item.systemObjectId && item.defaultDatabaseObjectId ? {
          name: "table",
          params: { systemObjectId: item.systemObjectId, databaseObjectId: item.defaultDatabaseObjectId }
        } : false;
      case 'report':
        return item.systemObjectId && item.embeddedReportId ? {
          name: "report",
          params: { systemObjectId: item.systemObjectId, embeddedReportId: item.embeddedReportId }
        } : false;
    }
    return false;
  }

  const menuTypes = ref([
    { name: 'Arbeidsliste', typeId: 2, objectType: 'system-object' },
    // Disabled for now, as we don't have a way to create a new database object'
    // {name: 'Oppslagstabell', typeId: 3, objectType: 'database-object'},
    { name: 'Rapport', typeId: 5, objectType: 'report' },
    { name: 'Mappe', typeId: 1, objectType: 'folder' },
  ]);


  const getMenuTypeName = computed(() => (objectType) =>
  {
    if (!objectType) {
      return '';
    }
    const menuType = menuTypes.value.find(item => item.objectType === objectType);
    return menuType ? menuType.name : '';
  });

  const isRouteValid = (item) =>
  {
    return getRoute(item) !== false;
  }

  const getIcon = (objectType) =>
  {
    if (!objectType) {
      return { icon: 'question-circle', color: 'var(--color-primary)', variant: 'fal' };
    }

    switch (objectType) {
      case 'folder':
        return { icon: 'folder-closed', color: 'var(--color-folder)', variant: 'fad' };
      case 'system-object':
        return { icon: 'memo-circle-check', color: 'var(--color-primary)', variant: 'fal' };
      case 'database-object':
        return { icon: 'memo-circle-info', color: 'var(--color-primary)', variant: 'fal' };
      case 'report':
        return { icon: 'chart-line-up', color: 'var(--color-primary)', variant: 'fal' };
    }
    return { icon: 'question-circle', color: 'var(--color-primary)', variant: 'fal' };
  }

  // Menu  and Tree *********************************************************************
  // Henter alle menyer på nytt fra basen
  async function refreshMenusAndTree ()
  {
    await getMenu();
  }

  async function getMenu ()
  {
    const response = await axiosClient.get('v2/menu');
    menuItems.value = response?.data;
    mapOfSystemObjects.value = createMapOfSystemObjects();
    return response?.data;
  }
// create map of all system objects with SystemObjectId as key
  function createMapOfSystemObjects ()
  {
    const map = new Map();
    for (const item of menuItems.value) {
      getAllSystemObjects(item, map);
    }
    return map;
  }

  async function getSystemObject (systemObjectId)
  {
    if (!mapOfSystemObjects.value)
      await getMenu();
    return mapOfSystemObjects.value.get(systemObjectId);
  }

  // fetch bread crumb path for a given item. Uses work list to lookup database object names and the menuitems for looking up
  // system object names
  const activeParentIds = ref(new Set());

  function getBreadcrumbForItem (systemObjectId, databaseObjectId)
  {
    const path = [];
    activeParentIds.value.clear();

    // Sjekk om mapOfSystemObjects er initialisert
    if (!mapOfSystemObjects.value) {
      console.warn("getBreadcrumbForItem: mapOfSystemObjects not initialized yet");
      return [];
    }

    const databaseObject = workListMenu.value.find(item => item.databaseObjectId === databaseObjectId)
    if (databaseObject != null) {
      path.push({ label: databaseObject.itemName })
    }

    const item = mapOfSystemObjects.value.get(Number(systemObjectId));
    if (item == null) {
      console.error("breadcrumb - failed looking up SystemObject for id", systemObjectId, mapOfSystemObjects)
      return [];
    }
    path.push({ label: item.name });

    let parentId = item.parentId;
    while (parentId != null) {
      const current = mapOfSystemObjects.value.get(parentId);
      if (current == null)
        break;
      path.push({ label: current.name });
      activeParentIds.value.add(parentId);
      parentId = current.parentId;
    }

    const finalPath = path.reverse();
    return finalPath;
  }

  // Add new state
  const breadcrumbs = ref([])

  // Add new function to update breadcrumbs
  function updateBreadcrumbs (systemObjectId, databaseObjectId)
  {
    if (!systemObjectId) {
      breadcrumbs.value = [];
      return;
    }

    const path = getBreadcrumbForItem(systemObjectId, databaseObjectId);
    // Filter out any items where label is undefined
    const filteredPath = path.filter(item => item.label !== undefined);
    breadcrumbs.value = filteredPath;
  }

  // Brukes i alle oppsett dialogene
  // Brukes til å flytte et system-object i menyen
  // MenuEdit *********************************************************************

  // Brukes til å opprette/endre meny punkt (system-object)
  async function MenuItemSave (item)
  {
    isProcessing.value.save = true;
    try {
      const result = await axiosClient.post('v2/system-object', item);
      if (result) {
        await refreshMenusAndTree();
        notificationsStore.addSuccess("Menyelement ble opprettet/oppdatert.");
        return true;
      }
      return false;
    } finally {
      isProcessing.value.save = false;
    }
  }

  // Brukes til å slette meny punkt (system-object)
  async function MenuItemDelete (systemObjectId)
  {
    isProcessing.value.delete = true;
    try {
      const result = await axiosClient.delete(`v2/system-object/${systemObjectId}`);
      if (result) {
        await refreshMenusAndTree();

        // Invalider worklist search cache slik at ikoner oppdateres korrekt
        try {
          const worklistSearch = useWorklistSearchStore();
          worklistSearch.clearSearch();
        } catch (error) {
          // Ignorer hvis store ikke er tilgjengelig
          console.debug('Could not clear worklist search cache:', error);
        }

        notificationsStore.addSuccess("Menyelement ble slettet.");
        return true;
      }
      return false;
    } catch (error) {
      // Vis backend-feilmelding hvis validering feiler
      const message = error.response?.data?.message || error.message || 'Ukjent feil ved sletting';
      notificationsStore.addError(message, 'Kunne ikke slette menyelement');
      return false;
    } finally {
      isProcessing.value.delete = false;
    }
  }

  // Brukes til å gjenopprette slettet meny punkt (system-object)
  async function MenuItemUnDelete (systemObjectId)
  {
    isProcessing.value.undelete = true;
    try {
      const result = await axiosClient.post(`v2/system-object/${systemObjectId}/undelete`);
      if (result) {
        await refreshMenusAndTree();

        // Invalider worklist search cache slik at ikoner oppdateres korrekt
        try {
          const worklistSearch = useWorklistSearchStore();
          worklistSearch.clearSearch();
        } catch (error) {
          // Ignorer hvis store ikke er tilgjengelig
          console.debug('Could not clear worklist search cache:', error);
        }

        notificationsStore.addSuccess("Menyelement ble gjenopprettet.");
        return true;
      }
      return false;
    } finally {
      isProcessing.value.undelete = false;
    }
  }

  async function MenuSaveNewOrder (items)
  {
    isProcessing.value.reorder = true;
    try {
      const result = await axiosClient.post(`v2/system-object/order`, items);
      if (result) {
        await refreshMenusAndTree();
        notificationsStore.addSuccess("Rekkefølgen på menyelementene ble oppdatert.");
        return true;
      }
      return false;
    } finally {
      isProcessing.value.reorder = false;
    }
  }

  // WorkListMenu *********************************************************************
  const workListMenu = computed(() => [
    ...(databaseObjects.value.length ? [{ label: 'Arbeidslister:', items: databaseObjects.value }] : []),
    ...(savedQueries.value.length ? [{ label: 'Brukerspørringer: ', items: savedQueries.value }] : [])
  ]);
  const workListMenuCount = computed(() => workListMenu.value.reduce((acc, curr) => acc + curr.items.length, 0));
  const databaseObjects = ref([]);
  const savedQueries = ref([]);

  // For å kunne velge arbeidslistene (Visning dropdown) til et system-object i hoved-visningen
  async function WorkListMenu_Hent (systemObjectId)
  {
    try {
      // Først hent tableDefinition for systemObject
      const tableDefResponse = await axiosClient.get('v2/table', {
        params: { systemObjectId }
      });

      if (!tableDefResponse?.data) {
        globalStore.debug('WorkListMenu_Hent: Ingen tableDefinition funnet for systemObjectId:', systemObjectId);
        databaseObjects.value = [];
        return;
      }

      const tableDefinitionId = tableDefResponse.data.tableDefinitionId;

      // Hent arbeidslister basert på tableDefinitionId (ikke systemObjectId)
      const response = await axiosClient.get('v2/database/object', {
        params: { tableDefinitionId }
      });

      databaseObjects.value = response?.data?.map(item => ({
        ...item,
        itemType: 'databaseObject',
        label: item.itemName,
        to: {
          name: 'table',
          params: {
            systemObjectId: systemObjectId,
            databaseObjectId: item.databaseObjectId
          }
        }
      }));

      const savedQueryResponse = await axiosClient.get(`v2/database/object/${systemObjectId}/saved-queries`);
      if (savedQueryResponse?.data) {
        savedQueries.value = savedQueryResponse.data.map(item => ({
          ...item,
          itemType: 'userQuery',
          label: item.queryName
        }));
      }
    } catch (error) {
      globalStore.debug('WorkListMenu_Hent error:', error);
      notificationsStore.addError('Kunne ikke laste arbeidslister', 'Feil ved lasting av arbeidsliste-meny');
      databaseObjects.value = [];
    }
  }

  const isProcessing = ref({
    save: false,
    delete: false,
    undelete: false,
    reorder: false
  });

  return {
    refreshMenusAndTree,
    MenuItemSave, MenuItemDelete, MenuItemUnDelete,
    WorkListMenu_Hent, workListMenu,
    menuItems, getMenu, getIcon, getRoute,
    getBreadcrumbForItem,
    getSystemObject,
    isRouteValid,
    activeParentIds,
    mapOfSystemObjects,
    MenuSaveNewOrder,
    isProcessing,
    breadcrumbs,
    updateBreadcrumbs,
    savedQueries,
    workListMenuCount,
    menuTypes,
    getMenuTypeName
  };

})
